import {
  getStackType,
  hasDeployStack,
  selectIsAccountOwner,
  selectStackById,
  selectStacksByOrgAsList,
} from "@app/deploy";
import { createSupportTicket, resetSupportTicket } from "@app/deploy/support";
import { selectOrganizationSelected } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useSelector,
} from "@app/react";
import { stackDetailVpcPeeringsUrl } from "@app/routes";
import { selectCurrentUser } from "@app/users";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useValidator } from "../hooks";
import {
  Banner,
  Box,
  Button,
  ExternalLink,
  IconAlertTriangle,
  IconExternalLink,
  Input,
  Select,
  Tooltip,
} from "../shared";
import { FormGroup } from "../shared/form-group";

function isConnectionIdValid(id: string): boolean {
  const t = id.trim();
  return t.length > 0 && t.toLowerCase().startsWith("pcx-");
}

const vpcPeeringValidators = {
  connectionId: (data: { connectionId: string }) => {
    const id = data.connectionId.trim();
    if (id === "") return "VPC Peering Connection ID is required";
    if (!isConnectionIdValid(data.connectionId)) {
      return "VPC Peering Connection ID must start with pcx-";
    }
  },
};

export const CreateVpcPeeringRequestPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const { id: routeStackId = "" } = useParams();
  const stackId = routeStackId || params.get("stack_id") || "";

  const org = useSelector(selectOrganizationSelected);
  const user = useSelector(selectCurrentUser);
  const stack = useSelector((s) => selectStackById(s, { id: stackId }));
  const isShared = getStackType(stack) === "shared";
  const isAccountOwner = useSelector((s) =>
    selectIsAccountOwner(s, { orgId: org.id }),
  );

  const [flow, setFlow] = useState<"aws" | "stack">("aws");
  const [connectionId, setConnectionId] = useState("");
  const [description, setDescription] = useState("");
  const [selectedStackId, setSelectedStackId] = useState("");

  const [errors, validate] = useValidator<
    { connectionId: string },
    typeof vpcPeeringValidators
  >(vpcPeeringValidators);

  // Dedicated stacks list (exclude current)
  const stacks = useSelector(selectStacksByOrgAsList);
  const dedicatedStacks = useMemo(
    () =>
      stacks.filter((s) => getStackType(s) === "dedicated" && s.id !== stackId),
    [stacks, stackId],
  );

  // Mock helpers until API provides values
  const generateStackCidr = (sid: string): string => {
    let hash = 0;
    for (let i = 0; i < sid.length; i++) {
      const char = sid.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    const octet = (Math.abs(hash) % 254) + 1;
    return `10.${octet}.0.0/16`;
  };
  const generateVpcId = (sid: string): string => {
    let hash = 0;
    for (let i = 0; i < sid.length; i++) {
      hash = (hash * 31 + sid.charCodeAt(i)) | 0;
    }
    const suffix = Math.abs(hash).toString(16).padStart(8, "0").slice(0, 8);
    return `vpc-${suffix}`;
  };
  const isRealVpcId = Boolean(stack.vpcId);
  const isRealCidr = Boolean(stack.vpcCidr);
  const vpcId = stack.vpcId || generateVpcId(stackId);
  const stackCidr = stack.vpcCidr || generateStackCidr(stackId);
  // Build plain text ticket body (no HTML) for support system consumption

  const loader = useLoader(createSupportTicket);
  useLoaderSuccess(loader, () => {
    navigate(stackDetailVpcPeeringsUrl(stackId), {
      state: { createSuccess: true },
    });
  });

  // Reset loader state on unmount to prevent stale success states
  useEffect(() => {
    return () => {
      dispatch(resetSupportTicket());
    };
  }, [dispatch]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Block submission if not account owner
    if (!isAccountOwner) {
      return;
    }
    // Block submission for AWS flow if real values are missing
    if (flow === "aws" && (!isRealVpcId || !isRealCidr)) {
      return;
    }

    if (flow === "aws" && !validate({ connectionId })) {
      return;
    }

    if (flow === "stack") {
      const subject = `VPC Peering Request (Dedicated Stack) - ${stack.name}`;
      const ticketDescription = `
<p><strong>VPC Peering Request (Dedicated Stack)</strong></p>

<p><strong>Details:</strong></p>
<ul>
  <li><strong>User:</strong> ${user.name} (${user.email})</li>
  <li><strong>Organization:</strong> ${org.name} (ID: ${org.id})</li>
  <li><strong>Primary Stack:</strong> ${stack.name} (ID: ${stack.id})</li>
  <li><strong>Peer Stack ID:</strong> ${selectedStackId}</li>
</ul>
      `.trim();

      dispatch(
        createSupportTicket({
          email: user.email,
          name: user.name,
          subject,
          description: ticketDescription,
          priority: "normal",
          attachments: [],
        }),
      );
    } else {
      // AWS flow - real values are guaranteed at this point due to validation above
      const subject = `VPC Peering Request (AWS) - ${stack.name}`;
      const cidrForEmail = stack.vpcCidr;
      const safeDesc = description.trim();
      const ticketDescription = `
<p>Your request for VPC Peering Connection has been received.</p>

<p><strong>Details:</strong></p>
<ul>
  <li><strong>User:</strong> ${user.name} (${user.email})</li>
  <li><strong>Org:</strong> ${org.name}</li>
  <li><strong>Stack:</strong> ${stack.name} (ID: ${stack.id})</li>
  <li><strong>Region:</strong> ${stack.region}</li>
  <li><strong>VPC Peering Connection ID:</strong> ${connectionId}</li>
  ${safeDesc ? `<li><strong>Description:</strong> ${safeDesc}</li>` : ""}
</ul>

<p><strong>Next steps:</strong> Once the connection has been accepted, use the following to configure route tables and security groups.</p>

<p>Using the following IP CIDR Range <code>${cidrForEmail}</code>:</p>
<ul>
  <li>Update the route table(s) for your VPC to map the IP Destination onto the new VPC Peering Target (it will be named ${connectionId}).</li>
  <li>If you want to accept connections from your Aptible apps into your VPC, add a rule to your VPC's security group(s) to allow connections from the appropriate IP range.</li>
</ul>
      `.trim();

      dispatch(
        createSupportTicket({
          email: user.email,
          name: user.name,
          subject,
          description: ticketDescription,
          priority: "normal",
          attachments: [],
        }),
      );
    }
  };

  // Redirect if not account owner
  useEffect(() => {
    if (org.id && !isAccountOwner) {
      navigate(stackDetailVpcPeeringsUrl(stackId));
    }
  }, [org.id, isAccountOwner, navigate, stackId]);

  if (!hasDeployStack(stack)) {
    return null;
  }

  if (isShared) {
    return (
      <Box>
        <h1 className="text-lg text-gray-500 font-semibold flex items-center gap-2 mb-4">
          <IconAlertTriangle color="#6B7280" />
          VPC Peering
        </h1>
        <Banner variant="info">
          VPC Peering is not available on Shared Stacks. Upgrade to a Dedicated
          Stack to peer with your AWS VPC or another Aptible stack.
        </Banner>
      </Box>
    );
  }

  // Show loading state while checking permissions
  if (!org.id || !isAccountOwner) {
    return null;
  }

  return (
    <div className="mb-4">
      <Box className="mb-4">
        <h2 className="text-lg text-black font-semibold">
          Create VPC Peering Request
        </h2>
        <p className="py-4 text-gray-600">
          Create a VPC peering request for the <strong>{stack.name}</strong>{" "}
          stack.
        </p>

        {flow === "aws" && (!isRealVpcId || !isRealCidr) && (
          <Banner variant="error" className="mb-4">
            VPC ID and CIDR information is required to submit a VPC peering
            request. Please contact Aptible Support to obtain these values.
          </Banner>
        )}

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <FormGroup label="Select VPC Target Destination" htmlFor="peer-type">
            <Select
              id="peer-type"
              value={flow}
              options={[
                { label: "Peer to AWS Account", value: "aws" },
                { label: "Peer to Aptible Dedicated Stack", value: "stack" },
              ]}
              onSelect={(opt) => setFlow(opt.value as "aws" | "stack")}
            />
          </FormGroup>

          {flow === "stack" && (
            <div className="space-y-2">
              {dedicatedStacks.length === 0 ? (
                <Banner variant="warning">
                  An additional Dedicated Stack is required to complete this
                  peering. Contact Aptible Support to provision another
                  Dedicated Stack, then return to create the peering request.
                </Banner>
              ) : null}
              <FormGroup label="Dedicated Stack" htmlFor="peer-stack">
                <Select
                  id="peer-stack"
                  value={selectedStackId}
                  options={[
                    { label: "Select a Stack", value: "", disabled: true },
                    ...dedicatedStacks.map((s) => ({
                      label: s.name,
                      value: s.id,
                    })),
                  ]}
                  onSelect={(opt) => setSelectedStackId(opt.value as string)}
                  disabled={dedicatedStacks.length === 0}
                />
              </FormGroup>
            </div>
          )}

          {flow === "aws" && (
            <div className="space-y-6">
              <h2 className="text-md font-semibold text-gray-900">
                Initiate VPC Peering connection
              </h2>

              <div className="space-y-1">
                <p className="font-semibold">
                  1. Create Peering Connection in AWS
                </p>
                <p className="text-sm text-gray-600">
                  Go to the{" "}
                  <ExternalLink
                    className="underline"
                    href={`https://console.aws.amazon.com/vpc/home?region=${stack.region}#PeeringConnections:sort=desc:peeredOwner`}
                  >
                    Peering Connections page of the AWS VPC Dashboard
                    <IconExternalLink
                      variant="sm"
                      className="inline ml-1 opacity-60"
                    />
                  </ExternalLink>{" "}
                  and click "Create VPC Peering Connection".
                </p>
              </div>

              <div className="space-y-1">
                <p className="font-semibold">2. Connect to Aptible's VPC</p>
                <p className="text-sm text-gray-600">
                  Select your own Local VPC, and then enter the following
                  details for the Aptible VPC to peer with.
                </p>
                <Banner variant="info" className="mt-2">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold">
                        Aptible VPC Information
                      </p>
                      <Tooltip text="These are your Aptible-side details for VPC Peering. Use them when initiating a peering connection from your own AWS VPC.">
                        <div className="flex-shrink-0 w-4 h-4 rounded-full bg-gray-400 text-white flex items-center justify-center text-xs font-bold cursor-help">
                          ?
                        </div>
                      </Tooltip>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                      <p className="text-sm font-medium">
                        AWS Account ID:{" "}
                        <code className="bg-black-100 px-1 py-0.5 rounded">
                          {stack.awsAccountId}
                        </code>
                      </p>
                      <p className="text-sm font-medium">
                        VPC ID:{" "}
                        <code className="bg-black-100 px-1 py-0.5 rounded">
                          {vpcId}
                        </code>
                        {!isRealVpcId ? (
                          <span className="text-xs text-gray-500 ml-1">
                            (mock data)
                          </span>
                        ) : null}
                      </p>
                      <p className="text-sm font-medium">
                        Region:{" "}
                        <code className="bg-black-100 px-1 py-0.5 rounded">
                          {stack.region}
                        </code>
                      </p>
                      <p className="text-sm font-medium">
                        CIDR:{" "}
                        <code className="bg-black-100 px-1 py-0.5 rounded">
                          {stackCidr}
                        </code>
                        {!isRealCidr ? (
                          <span className="text-xs text-gray-500 ml-1">
                            (mock data)
                          </span>
                        ) : null}
                      </p>
                    </div>
                  </div>
                </Banner>
              </div>

              <div className="space-y-2">
                <h2 className="text-md font-semibold text-gray-900">
                  Provide VPC Peering Details
                </h2>
                <p className="text-sm text-gray-600">
                  Provide your VPC details so Aptible can accept the peering
                  request.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormGroup
                    label="VPC Peering Connection ID"
                    htmlFor="connection-id"
                    labelProps={{ className: "text-sm font-semibold" }}
                    feedbackMessage={errors.connectionId}
                    feedbackVariant={errors.connectionId ? "danger" : "info"}
                  >
                    <Input
                      id="connection-id"
                      name="connection-id"
                      value={connectionId}
                      onChange={(e) => setConnectionId(e.currentTarget.value)}
                      placeholder="pcx-1234567890abcdef0"
                    />
                  </FormGroup>
                  <FormGroup
                    label="Description (Optional)"
                    htmlFor="description"
                    labelProps={{ className: "text-sm font-semibold" }}
                  >
                    <Input
                      id="description"
                      name="description"
                      value={description}
                      onChange={(e) => setDescription(e.currentTarget.value)}
                      placeholder="Brief description of the peering connection"
                    />
                  </FormGroup>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              type="button"
              variant="white"
              onClick={() => navigate(stackDetailVpcPeeringsUrl(stackId))}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                flow === "stack"
                  ? selectedStackId === ""
                  : !isConnectionIdValid(connectionId) ||
                    !isRealVpcId ||
                    !isRealCidr
              }
              isLoading={loader.isLoading}
            >
              Submit
            </Button>
          </div>
        </form>

        {loader.isSuccess && (
          <Banner variant="success" className="mt-4">
            VPC Peering request submitted successfully! The Aptible Team will
            process your request.
          </Banner>
        )}
      </Box>
    </div>
  );
};
